import { AbstractControl, ValidationErrors } from '@angular/forms';

/**
 * The password rules for POST /api/auth/reset-password, mirrored from the
 * backend (ControlTower ResetPasswordRequestDTO + PasswordValidatorService):
 *
 *   ^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,12}$
 *
 * The server also refuses a password that contains the account's email or
 * username, or matches a recent password. The client can't check those (the
 * reset page only knows a masked email), so the form states them as a note.
 *
 * The client must be at least as strict as the server: every rejected
 * password uses one of the 5 attempts a reset link allows (423 after that).
 */

export const RESET_PASSWORD_MIN = 8;
export const RESET_PASSWORD_MAX = 12;
export const RESET_PASSWORD_SYMBOLS = '!@#$%^&*';

const ALLOWED = /^[A-Za-z\d!@#$%^&*]$/;

export type PasswordRuleId = 'length' | 'lower' | 'upper' | 'digit' | 'symbol' | 'allowed';

/** pending: not met yet (grey). ok: met (green). bad: broken in a way more typing won't fix (red). */
export type PasswordRuleState = 'pending' | 'ok' | 'bad';

export interface PasswordRuleCheck {
  id: PasswordRuleId;
  label: string;
  state: PasswordRuleState;
  /** Short extra: the running length. */
  meta?: string;
  /** What to change when the rule is broken. */
  fix?: string;
}

function describeChar(char: string): string {
  if (char === ' ') return 'spaces';
  if (/\s/.test(char)) return 'tabs or line breaks';
  return char;
}

export function checkResetPassword(value: string | null | undefined): PasswordRuleCheck[] {
  const password = value ?? '';
  const length = [...password].length;
  const disallowed = Array.from(new Set([...password].filter((char) => !ALLOWED.test(char))));

  const has = (pattern: RegExp): PasswordRuleState => (pattern.test(password) ? 'ok' : 'pending');

  let lengthState: PasswordRuleState = 'pending';
  let lengthFix: string | undefined;
  if (length > RESET_PASSWORD_MAX) {
    lengthState = 'bad';
    const over = length - RESET_PASSWORD_MAX;
    lengthFix = `Remove ${over} ${over === 1 ? 'character' : 'characters'}.`;
  } else if (length >= RESET_PASSWORD_MIN) {
    lengthState = 'ok';
  }

  return [
    {
      id: 'length',
      label: `${RESET_PASSWORD_MIN} to ${RESET_PASSWORD_MAX} characters`,
      state: lengthState,
      meta: length > 0 ? `${length} so far` : undefined,
      fix: lengthFix,
    },
    { id: 'lower', label: 'A lowercase letter', state: has(/[a-z]/) },
    { id: 'upper', label: 'An uppercase letter', state: has(/[A-Z]/) },
    { id: 'digit', label: 'A number', state: has(/\d/) },
    { id: 'symbol', label: 'One of these symbols', state: has(/[!@#$%^&*]/) },
    {
      id: 'allowed',
      label: 'Nothing else: no spaces or other symbols',
      state: disallowed.length ? 'bad' : length > 0 ? 'ok' : 'pending',
      fix: disallowed.length
        ? `Remove ${disallowed.map(describeChar).join(' ')}.`
        : undefined,
    },
  ];
}

/** Form validator: `{ passwordPolicy: { failed: [...] } }` until every rule is met. */
export function resetPasswordPolicyValidator(control: AbstractControl): ValidationErrors | null {
  const value: string = control.value ?? '';
  if (!value) return null; // `required` reports the empty case.
  const failed = checkResetPassword(value)
    .filter((rule) => rule.state !== 'ok')
    .map((rule) => rule.id);
  return failed.length ? { passwordPolicy: { failed } } : null;
}
